import { parse, formatHex, interpolate, wcagContrast, Color, formatRgb } from 'culori';

function clamp01(value: number): number {
    return Math.min(1, Math.max(0, value));
}

function validateColor(input: string | Color): Color {
    if (typeof input === 'string') {
        const parsed = parse(input);
        if (!parsed) throw new Error(`Invalid color string: ${input}`);
        return parsed;
    }
    return input;
}

export const CONTRAST_THRESHOLD = 3;

/**
 * Creates a lighter version (tint) of the given base color by mixing it with white.
 * The amount parameter controls how much white is added:
 *   - 0 means no change (original color)
 *   - 1 means fully white
 *
 * @param baseColor - The base color to tint, in any CSS-compatible format (e.g., hex)
 * @param amount - A number between 0 and 1 representing how much white to mix in
 * @returns A hex string representing the tinted color
 */
export function tint(baseColor: string, amount: number): string {
    const base = validateColor(baseColor);
    const white = validateColor('#ffffff');

    const mix = interpolate([base, white], 'oklab'); // Mix towards white using perceptual space
    return formatHex(mix(clamp01(amount)));
}

/**
 * Creates a darker version (shade) of the given base color by mixing it with black.
 * The amount parameter controls how much black is added:
 *   - 0 means no change (original color)
 *   - 1 means fully black
 *
 * @param baseColor - The base color to shade, in any CSS-compatible format (e.g., hex)
 * @param amount - A number between 0 and 1 representing how much black to mix in
 * @returns A hex string representing the shaded color
 */
export function shade(baseColor: string, amount: number): string {
    const base = validateColor(baseColor);
    const black = validateColor('#000000');

    const mix = interpolate([base, black], 'oklab'); // Mix towards black using perceptual space
    return formatHex(mix(clamp01(amount)));
}

/**
 * Checks whether the contrast ratio between a background and foreground color
 * meets or exceeds the WCAG AA contrast threshold (default: 4.5).
 *
 * Accepts colors as strings or Culori `Color` objects. Uses `wcagContrast` to
 * calculate the contrast ratio.
 *
 * @param backgroundColor - The background color to test
 * @param foregroundColor - The foreground (typically text) color to test
 * @returns `true` if the contrast ratio is sufficient; otherwise `false`
 */
export function checkContrast(backgroundColor: string | Color, foregroundColor: string | Color): boolean {
    const background = validateColor(backgroundColor);
    const foreground = validateColor(foregroundColor);
    const ratio = wcagContrast(background, foreground);
    return ratio >= CONTRAST_THRESHOLD;
}

/**
 * Returns a CSS-compatible color string with the specified opacity applied.
 *
 * @param color - The base color (hex, rgb, hsl, or Culori Color)
 * @param opacity - The alpha value to apply (0 to 1)
 * @returns A CSS color string in rgba() format
 */
export function alpha(color: string | Color, opacity: number): string {
    const validated = validateColor(color);
    validated.alpha = clamp01(opacity);
    return formatRgb(validated);
}
